import React, { useState } from 'react';
import { 
  Layout, Wand2, TrendingUp, DollarSign,
  Package, RefreshCw, Eye, Download, CheckCircle
} from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';

interface PlanogramPattern {
  id: string;
  name: string;
  description: string;
  expectedSalesIncrease: number;
  expectedProfitIncrease: number;
  changes: number;
  confidence: number;
  rules: string[];
}

interface ShelfItem {
  skuId: string;
  skuName: string;
  category: string;
  position: { shelf: number; row: number; column: number };
  facings: number;
  salesPerDay: number;
  profitMargin: number;
  turnoverRate: number;
  status: 'optimal' | 'underperforming' | 'overperforming' | 'dead';
}

interface PlanogramGeneratorProps {
  patterns: PlanogramPattern[];
  shelfItems: ShelfItem[];
}

const PlanogramGenerator: React.FC<PlanogramGeneratorProps> = ({ patterns, shelfItems }) => {
  const [selectedPattern, setSelectedPattern] = useState<string>(patterns[0]?.id || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [comparisonMode, setComparisonMode] = useState(false);
  
  const handleGenerate = () => {
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      setPreviewMode(true);
    }, 2000);
  };
  
  const handleApplyPattern = () => {
    console.log('Applying pattern:', selectedPattern);
    // パターン適用処理
  };
  
  // 現在と提案の棚割り可視化
  const renderShelfVisualization = (isProposed: boolean = false) => {
    const shelves = 5;
    const columns = 8;
    
    return (
      <div className="space-y-2">
        {[...Array(shelves)].map((_, shelfIdx) => (
          <div key={shelfIdx} className="flex gap-1">
            {[...Array(columns)].map((_, colIdx) => {
              const item = shelfItems.find(
                i => i.position.shelf === shelfIdx + 1 && 
                     i.position.column === colIdx + 1 && 
                     i.position.row === 2
              );
              
              // 提案時は位置変更をシミュレート
              const isChanged = isProposed && Math.random() > 0.7;
              
              return (
                <div
                  key={colIdx}
                  className="relative w-12 h-16 border rounded flex items-center justify-center text-xs cursor-pointer hover:shadow-lg transition-all"
                  style={{
                    borderColor: isChanged ? colors.accent.blue : colors.ui.border,
                    backgroundColor: item 
                      ? item.status === 'optimal' ? colors.support.success + '20' :
                        item.status === 'overperforming' ? colors.accent.blue + '20' :
                        item.status === 'underperforming' ? colors.status.warning + '20' :
                        colors.status.error + '20'
                      : colors.ui.bgSecondary,
                    borderWidth: isChanged ? 2 : 1
                  }}
                  title={item ? `${item.skuName}\n売上: ¥${Math.round(item.salesPerDay)}` : '空き'}
                >
                  {item ? (
                    <div className="text-center">
                      <div className="font-bold">{item.skuId.slice(-3)}</div>
                      <div style={{ fontSize: '10px' }}>
                        {item.facings}F
                      </div>
                    </div>
                  ) : '-'}
                  
                  {isChanged && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };
  
  return (
    <div className="space-y-6">
      {/* パターン選択 */}
      <div className="grid grid-cols-2 gap-4">
        {patterns.map(pattern => (
          <Card 
            key={pattern.id}
            className={`p-4 cursor-pointer transition-all ${
              selectedPattern === pattern.id ? 'ring-2' : 'hover:shadow-lg'
            }`}
            style={{
              ringColor: selectedPattern === pattern.id ? colors.accent.purple : undefined
            }}
            onClick={() => setSelectedPattern(pattern.id)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Wand2 size={18} style={{ color: colors.accent.purple }} />
                <h3 className="font-semibold" style={{ color: colors.text.primary }}>
                  {pattern.name}
                </h3>
              </div>
              {selectedPattern === pattern.id && (
                <CheckCircle size={18} style={{ color: colors.accent.purple }} />
              )}
            </div>
            
            <p className="text-sm mb-3" style={{ color: colors.text.secondary }}>
              {pattern.description}
            </p>
            
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp size={14} style={{ color: colors.support.success }} />
                <span className="text-sm">
                  売上 <strong>+{pattern.expectedSalesIncrease}%</strong>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign size={14} style={{ color: colors.accent.blue }} />
                <span className="text-sm">
                  利益 <strong>+{pattern.expectedProfitIncrease}%</strong>
                </span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RefreshCw size={14} style={{ color: colors.text.secondary }} />
                <span className="text-xs" style={{ color: colors.text.secondary }}>
                  {pattern.changes}箇所変更
                </span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full transition-all"
                    style={{ 
                      width: `${pattern.confidence}%`,
                      backgroundColor: pattern.confidence > 80 ? colors.support.success : 
                                     pattern.confidence > 60 ? colors.status.warning : 
                                     colors.status.error
                    }}
                  />
                </div>
                <span className="text-xs" style={{ color: colors.text.secondary }}>
                  {pattern.confidence}%
                </span>
              </div>
            </div>
            
            <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.ui.border }}>
              <h4 className="text-xs font-medium mb-2" style={{ color: colors.text.primary }}>
                適用ルール:
              </h4>
              <ul className="space-y-1">
                {pattern.rules.map((rule, idx) => (
                  <li key={idx} className="text-xs flex items-start gap-1" style={{ color: colors.text.secondary }}>
                    <span>•</span>
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Card>
        ))}
      </div>
      
      {/* アクションボタン */}
      <div className="flex items-center gap-4">
        <Button 
          variant="primary" 
          onClick={handleGenerate}
          disabled={!selectedPattern || isGenerating}
        >
          {isGenerating ? (
            <>
              <RefreshCw size={16} className="animate-spin" />
              生成中...
            </>
          ) : (
            <>
              <Wand2 size={16} />
              Planogram生成
            </>
          )}
        </Button>
        
        <Button 
          variant="secondary"
          onClick={() => setComparisonMode(!comparisonMode)}
        >
          <Eye size={16} />
          {comparisonMode ? '単独表示' : '比較表示'}
        </Button>
        
        {previewMode && (
          <Button variant="secondary">
            <Download size={16} />
            エクスポート
          </Button>
        )}
      </div>
      
      {/* プレビューエリア */}
      {previewMode && (
        <div className={`grid ${comparisonMode ? 'grid-cols-2' : 'grid-cols-1'} gap-6`}>
          {/* 現在の棚割り */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold" style={{ color: colors.text.primary }}>
                現在の棚割り
              </h3>
              <Badge variant="secondary">基準</Badge>
            </div>
            
            {renderShelfVisualization(false)}
            
            <div className="mt-4 pt-4 border-t" style={{ borderColor: colors.ui.border }}>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span style={{ color: colors.text.secondary }}>平均売上</span>
                  <div className="font-bold">¥125K/日</div>
                </div>
                <div>
                  <span style={{ color: colors.text.secondary }}>粗利率</span>
                  <div className="font-bold">22.5%</div>
                </div>
                <div>
                  <span style={{ color: colors.text.secondary }}>回転率</span>
                  <div className="font-bold">12.3回</div>
                </div>
              </div>
            </div>
          </Card>
          
          {/* 提案の棚割り */}
          {comparisonMode && (
            <Card className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold" style={{ color: colors.text.primary }}>
                  提案の棚割り
                </h3>
                <Badge variant="primary">最適化後</Badge>
              </div>
              
              {renderShelfVisualization(true)}
              
              <div className="mt-4 pt-4 border-t" style={{ borderColor: colors.ui.border }}>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span style={{ color: colors.text.secondary }}>予測売上</span>
                    <div className="font-bold text-green-600">¥144K/日</div>
                  </div>
                  <div>
                    <span style={{ color: colors.text.secondary }}>予測粗利率</span>
                    <div className="font-bold text-green-600">25.2%</div>
                  </div>
                  <div>
                    <span style={{ color: colors.text.secondary }}>予測回転率</span>
                    <div className="font-bold text-green-600">14.1回</div>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}
      
      {/* 変更詳細 */}
      {previewMode && (
        <Card className="p-4">
          <h3 className="font-semibold mb-4" style={{ color: colors.text.primary }}>
            主な変更点
          </h3>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 rounded" style={{ backgroundColor: colors.ui.bgSecondary }}>
              <Package size={18} style={{ color: colors.accent.purple }} />
              <div className="flex-1">
                <div className="font-medium mb-1">売上上位商品の再配置</div>
                <div className="text-sm" style={{ color: colors.text.secondary }}>
                  上位20商品をアイレベル（棚2-3段目）に集約し、視認性と手に取りやすさを改善
                </div>
              </div>
              <Badge variant="success">+8% 売上</Badge>
            </div>
            
            <div className="flex items-start gap-3 p-3 rounded" style={{ backgroundColor: colors.ui.bgSecondary }}>
              <TrendingUp size={18} style={{ color: colors.support.success }} />
              <div className="flex-1">
                <div className="font-medium mb-1">フェーシング最適化</div>
                <div className="text-sm" style={{ color: colors.text.secondary }}>
                  売上実績に基づきフェース数を調整。高回転商品は2-3フェースに拡大
                </div>
              </div>
              <Badge variant="success">+5% 効率</Badge>
            </div>
            
            <div className="flex items-start gap-3 p-3 rounded" style={{ backgroundColor: colors.ui.bgSecondary }}>
              <Layout size={18} style={{ color: colors.accent.blue }} />
              <div className="flex-1">
                <div className="font-medium mb-1">関連商品のグループ化</div>
                <div className="text-sm" style={{ color: colors.text.secondary }}>
                  クロスセル効果の高い商品を隣接配置し、買い回りを促進
                </div>
              </div>
              <Badge variant="success">+4% 客単価</Badge>
            </div>
          </div>
          
          {previewMode && (
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setPreviewMode(false)}>
                キャンセル
              </Button>
              <Button variant="primary" onClick={handleApplyPattern}>
                この提案を適用
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default PlanogramGenerator;
